import { ReactNode, useEffect, useMemo, useState } from "react";
import { IAssistantData } from "../../../../entities/assistant";
import { IIdmStaffMember } from "../../../../entities/idm";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { ITutorData, tutorService } from "../../../../entities/tutor";
import { IUserPersonalData } from "../../../../entities/user";
import { SanctionSequenceReset } from "../../../../features/sanctionSequenceReset";
import { useAppSelector } from "../../../../app/store/store";
import { formatTutorConversion } from "../../../../shared/lib/helpers/formatTutorLoad";
import classes from "../staff/staffWidget.module.scss";

interface IUseStaffProfileViewModelParams {
    selectedStaffId: number | null;
    staffMember: IIdmStaffMember;
    userPersonalData: IUserPersonalData | null;
}

export const useStaffProfileViewModel = ({
    selectedStaffId,
    staffMember,
    userPersonalData,
}: IUseStaffProfileViewModelParams) => {
    const {my} = useAppSelector(state => state.myReducer)
    const [tutorProfile, setTutorProfile] = useState<ITutorData | null>(null)
    const [isTutorProfileLoading, setIsTutorProfileLoading] = useState<boolean>(false)
    const [resolvedTutorStaffId, setResolvedTutorStaffId] = useState<number | null>(null)

    const normalizedRoleNames = useMemo(
        () => staffMember.roles.map(role => role.role_name.trim().toLowerCase()),
        [staffMember.roles]
    )
    const normalizedRoleCodes = useMemo(
        () => staffMember.roles.map(role => role.role_code.trim().toLowerCase()).filter(Boolean),
        [staffMember.roles]
    )
    const isTutorStaff = normalizedRoleCodes.includes('tutor')
        || normalizedRoleNames.some(roleName => roleName.includes('репетитор') || roleName.includes('tutor'))
    const isAssistantStaff = normalizedRoleCodes.includes('assistant')
        || normalizedRoleNames.some(roleName => roleName.includes('ассистент') || roleName.includes('assistant'))
    const isShiftStaff = normalizedRoleCodes.some(roleCode => ['coordinator', 'curator'].includes(roleCode))
    const isHrStaff = normalizedRoleCodes.includes('hr')
        || normalizedRoleNames.some(roleName => roleName === 'hr' || roleName.includes('hr'))

    useEffect(() => {
        if(!isTutorStaff || !selectedStaffId){
            setTutorProfile(null)
            setResolvedTutorStaffId(null)
            return
        }

        let isActual = true

        const loadTutorProfile = async () => {
            try{
                setIsTutorProfileLoading(true)
                const tutor = await tutorService.getByUserId(selectedStaffId)
                if(isActual){
                    setTutorProfile(tutor)
                }
            }
            catch(error){
                if(isActual){
                    console.log(error)
                    setTutorProfile(null)
                }
            }
            finally{
                if(isActual){
                    setResolvedTutorStaffId(selectedStaffId)
                    setIsTutorProfileLoading(false)
                }
            }
        }

        loadTutorProfile()

        return () => {
            isActual = false
        }
    }, [isTutorStaff, selectedStaffId])

    const tutorUserId = userPersonalData?.user_id || tutorProfile?.user_id || staffMember.user_id || 0
    const tutorLegacyId = tutorProfile?.legacy_tutor_id || 0
    const canResetTutorSanctionSequence = hasFeature(my, FEATURES.TUTORS.SANCTION_SEQUENCE_RESET, tutorUserId)
    const tutorTgLink = userPersonalData?.tg || tutorProfile?.tg || ''
    const tutorWorkTg = userPersonalData?.work_tgs?.[0]?.name || tutorProfile?.work_tg_name || ''
    const tutorHeaderData = useMemo<ITutorData | null>(() => {
        if(!userPersonalData && !tutorProfile){
            return null
        }

        const personalWorkTgIds = (userPersonalData?.work_tgs || [])
            .map(workTg => workTg.id)
            .filter(id => id > 0)

        return {
            id: tutorUserId,
            user_id: tutorUserId,
            legacy_tutor_id: tutorLegacyId || undefined,
            full_name: userPersonalData?.full_name || tutorProfile?.full_name || staffMember.full_name,
            tg: userPersonalData?.tg || tutorProfile?.tg || '',
            email: userPersonalData?.email || tutorProfile?.email || '',
            phone: userPersonalData?.phone || tutorProfile?.phone || '',
            tag_ids: tutorProfile?.tag_ids ?? userPersonalData?.tag_ids ?? [],
            cost_per_hour: userPersonalData?.cost_per_hour || tutorProfile?.cost_per_hour || '',
            balance: tutorProfile?.balance,
            created_at: userPersonalData?.created_at || tutorProfile?.created_at,
            work_tg_name: tutorWorkTg,
            work_tg_ids: personalWorkTgIds.length > 0 ? personalWorkTgIds : (tutorProfile?.work_tg_ids || []),
            is_archive: Boolean(tutorProfile?.is_archive),
            archive_comment: tutorProfile ? tutorProfile.archive_comment : '',
            labels: tutorProfile?.labels || [],
            desired_hours: tutorProfile?.desired_hours || '',
            actual_hours: tutorProfile?.actual_hours || '',
            gender: tutorProfile?.gender || userPersonalData?.gender || '',
            conversion: tutorProfile?.conversion ?? 0,
            conversion_base: tutorProfile?.conversion_base ?? '0',
            conversion_closed_count: tutorProfile?.conversion_closed_count,
            conversion_lost_count: tutorProfile?.conversion_lost_count,
            sanction_sequence_number: tutorProfile?.sanction_sequence_number ?? null,
        }
    }, [staffMember.full_name, tutorLegacyId, tutorProfile, tutorUserId, tutorWorkTg, userPersonalData])
    const tutorDataList = useMemo(() => ([
        `ID: ${tutorUserId || 'Не указан'}`,
        `ФИО: ${userPersonalData?.full_name || tutorProfile?.full_name || staffMember.full_name || 'Не указано'}`,
        `Дата регистрации: ${userPersonalData?.created_at || tutorProfile?.created_at || 'Не указана'}`,
        `Телефон: ${userPersonalData?.phone || tutorProfile?.phone || staffMember.phone || 'Не указан'}`,
        `Email: ${userPersonalData?.email || tutorProfile?.email || staffMember.email || 'Не указан'}`,
        userPersonalData?.cost_per_month && !isTutorStaff ? `Зарплата в месяц: ${userPersonalData.cost_per_month} ₽` : null,
        userPersonalData?.cost_per_month && isTutorStaff ? `Вознаграждение в месяц: ${userPersonalData.cost_per_month} ₽` : null,
        `${userPersonalData?.cost_per_hour || tutorProfile?.cost_per_hour ? `Стоимость часа: ${userPersonalData?.cost_per_hour || tutorProfile?.cost_per_hour} ₽` : 'Стоимость часа: Не указана'}`,
        `Баланс: ${tutorProfile?.balance ? `${tutorProfile.balance} ₽` : '—'}`,
        `План/факт: ${tutorProfile?.desired_hours || '—'}/${tutorProfile?.actual_hours || '0'}`,
        formatTutorConversion(tutorProfile?.conversion ?? 0, tutorProfile?.conversion_base ?? '0', tutorProfile?.conversion_closed_count, tutorProfile?.conversion_lost_count),
        <SanctionSequenceReset
            value={tutorProfile?.sanction_sequence_number}
            canEdit={canResetTutorSanctionSequence}
            onUpdate={async (value) => {
                await tutorService.updateSanctionSequence(tutorUserId, value)
                setTutorProfile(await tutorService.getByUserId(tutorUserId))
            }}
        />,
        tutorTgLink ? (
            <a
                key="tutor-tg-link"
                target="_blank"
                rel="noreferrer"
                href={tutorTgLink}
                className={classes.tgLink}
            >
                <svg className={classes.tgLinkIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.33366 9.66634L9.66699 6.33301" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M5.00033 4.33301H4.33366C3.59728 4.33301 3.00033 4.92996 3.00033 5.66634V11.6663C3.00033 12.4027 3.59728 12.9997 4.33366 12.9997H10.3337C11.07 12.9997 11.667 12.4027 11.667 11.6663V10.9997" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M8.33301 3H12.9997V7.66667" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                Написать в тг
            </a>
        ) : 'Telegram: не указан',
        tutorWorkTg ? `Рабочий аккаунт тг: ${tutorWorkTg}` : null,
        tutorProfile?.is_archive ? (
            <span className={classes.archiveStatus}>
                Статус: <span>В архиве</span>
            </span>
        ) : null,
        tutorProfile?.is_archive && tutorProfile.archive_comment.trim()
            ? <span className={classes.archiveComment}>
                Причина архивации: {tutorProfile.archive_comment}
            </span>
            : null,
    ].filter(Boolean) as ReactNode[]), [
        staffMember.email,
        staffMember.full_name,
        staffMember.phone,
        canResetTutorSanctionSequence,
        isTutorStaff,
        tutorProfile,
        tutorTgLink,
        tutorUserId,
        tutorWorkTg,
        userPersonalData,
    ])
    const assistantProfile = useMemo<IAssistantData | null>(() => {
        if(!isAssistantStaff || !selectedStaffId){
            return null
        }

        return {
            id: userPersonalData?.user_id || staffMember.user_id || selectedStaffId,
            full_name: userPersonalData?.full_name || staffMember.full_name || '',
            phone: userPersonalData?.phone || staffMember.phone || '',
            tg: userPersonalData?.tg || staffMember.tg || '',
            created_at: userPersonalData?.created_at || '',
            work_tgs: (userPersonalData?.work_tgs || []).map(workTg => ({
                id: workTg.id,
                name: workTg.name,
            })),
            permissions: {
                can_penalize_assistants: [],
                can_view_contracts: false,
            },
        }
    }, [isAssistantStaff, selectedStaffId, staffMember, userPersonalData])

    return {
        assistantProfile,
        canAssignIdmRoles: !normalizedRoleCodes.includes('tutor'),
        isAssistantStaff,
        isHrStaff,
        isShiftStaff,
        isTutorProfileLoading,
        isTutorProfileResolved: resolvedTutorStaffId === selectedStaffId,
        isTutorStaff,
        tutorDataList,
        tutorHeaderData,
        onTutorArchiveChange: setTutorProfile,
        onTutorSanctionSequenceReset: setTutorProfile,
        onTutorTagsChange: (tagIds: number[]) => {
            setTutorProfile(current => current ? {...current, tag_ids: tagIds} : current)
        },
        tutorLegacyId,
        tutorUserId,
    }
}
